import {DragDropContext} from '@wandb/weave/common/containers/DragDropContainer';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import React, {useContext, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {useHasBeenOnScreen, useSelector} from '../../../state/hooks';
import {usePanelComments} from '../../../state/reports/usePanelComments';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import type {Ref as PanelBankSectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {useConfigXBounds} from '../../Panel/hooks/hooks';
import {PanelContextProvider} from '../../Panel/PanelContextProvider';
import {PanelEditorContainer} from '../../Panel/PanelEditor/PanelEditorContainer';
import {PanelIDContextProvider} from '../../Panel/PanelIDContext';
import {SharedPanelStateContextProvider} from '../../Panel/SharedPanelStateContext';
import {usePanelViewTypeClass} from '../../Panel/usePanelViewTypeClass';
import {OrganizedSettings} from '../../PanelBank/types';
import {PanelCompRedux} from '../../PanelCompRedux';
import {ReportPanelHeader} from './ReportPanelHeader';

type ReportPanelContainerProps = {
  inheritedSettings: OrganizedSettings;
  isReadOnly: boolean | undefined;
  onContentHeightChange?: (height: number) => void;
  panelBankSectionConfigRef: PanelBankSectionRef;
  panelRef: PanelRef;
};

export const ReportPanelContainer = (props: ReportPanelContainerProps) => {
  const {ref: domRef, hasBeenOnScreen} = useHasBeenOnScreen();

  return (
    <div ref={domRef} style={{height: '100%', width: '100%'}}>
      {hasBeenOnScreen ? (
        <ReportPanel {...props} />
      ) : (
        <div className="h-full w-full border border-moon-300 bg-white" />
      )}
    </div>
  );
};

export const ReportPanel = ({
  inheritedSettings,
  isReadOnly,
  onContentHeightChange,
  panelBankSectionConfigRef,
  panelRef,
}: ReportPanelContainerProps) => {
  const configXBounds = useConfigXBounds(panelRef);

  // This is currently used to track panel setting analytics
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].__id__
  );

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  const {isHighlighted} = usePanelComments({panelRef});

  const {dragRef} = useContext(DragDropContext);
  const isSelected = useMemo(
    () => dragRef?.id === panelRef.id,
    [panelRef, dragRef]
  );

  const panelViewTypeClass = usePanelViewTypeClass(panelType);

  const panelClassNames = useMemo(() => {
    return classNames('editable-panel', panelViewTypeClass, {
      'editable-panel-markdown': panelType === 'Markdown Panel',
      'panel-selected': isSelected,
      'panel-highlighted': isHighlighted,
    });
  }, [panelViewTypeClass, panelType, isSelected, isHighlighted]);

  return (
    <PanelContextProvider
      inheritedSettings={inheritedSettings}
      isReadOnly={isReadOnly}
      panelBankSectionConfigRef={panelBankSectionConfigRef}
      panelRef={panelRef}>
      <PanelIDContextProvider stablePanelId={stablePanelId}>
        <SharedPanelStateContextProvider config={configXBounds}>
          <PanelEditorContainer currentHeight={0} />
          <TailwindContents>
            <div
              className={twMerge(panelClassNames, 'px-12 pb-12 pt-4')}
              data-test="report-panel-container">
              <ReportPanelHeader
                panelBankSectionConfigRef={panelBankSectionConfigRef}
              />
              <div
                className={twMerge(
                  'flex flex-col',
                  'h-full w-full grow-[1]',
                  'justify-center'
                )}
                id={'editable-panel-' + stablePanelId}>
                <PanelCompRedux
                  configMode={false}
                  currentHeight={0}
                  dimensions={undefined as any}
                  onContentHeightChange={onContentHeightChange}
                  panelRef={panelRef}
                  readOnly={isReadOnly}
                />
              </div>
            </div>
          </TailwindContents>
        </SharedPanelStateContextProvider>
      </PanelIDContextProvider>
    </PanelContextProvider>
  );
};
